import { useMutation, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { type SimpleWorkspace } from '@opallabs/concepts'
import { OpalApiResponseDocument, OpalApiRequestBody } from '../../../schema'
import { customFields } from '../../../v3/customFields'
import { globalQueryKeys } from '../../queryKeys'
import { customFieldsCollectionParams } from './selectors'
import { type FlattenedCustomField } from './types'

type RequestBody = OpalApiRequestBody<'UpdateCustomFieldV3'>
type RequestBodyParams = Readonly<
  Partial<
    Pick<
      FlattenedCustomField,
      | 'defaultNotificationMode'
      | 'description'
      | 'isCascading'
      | 'isColorDisabled'
      | 'isPaired'
      | 'metadata'
      | 'name'
      | 'pairedLabelSetId'
    >
  > & {
    fieldId: FlattenedCustomField['id']
    isDeactivated?: FlattenedCustomField['isInactive']
  }
>

function buildRequestBody({
  fieldId,
  name,
  metadata,
  description,
  isDeactivated,
  isColorDisabled,
  isCascading,
  isPaired,
  defaultNotificationMode,
  pairedLabelSetId,
}: RequestBodyParams): RequestBody {
  // @note `RequestBodyParams`, and the `FlattenedCustomField` it's built from, don't include the full relationships
  // object, just the deprecated `metadata` version, so it's not possible to manipulate the real existing relationships.
  // (And adding the relationships to `FlattenedCustomField` probably wouldn't be worth the overhead.) Fortunately
  // `react-query`, or something else in the chain that builds the request, seems to automatically drop the
  // `paired_label_set` relationship if its `data` is set to `null`, and just send all the other relationships; the
  // backend then sees that the `paired_label_set` relationship is missing, and uses that as the signal to remove the
  // relationship internally.
  //
  // The `isPaired === false` branch only runs when explicitly unpairing, and `pairedLabelSetId` only runs when
  // establishing a new pairing, so editing other parts of the data won't accidentally unpair the field.
  const relationships: RequestBody['data']['relationships'] = {}

  if (isPaired === false) {
    relationships.paired_label_set = { data: null }
  } else if (pairedLabelSetId) {
    relationships.paired_label_set = {
      data: { id: pairedLabelSetId, type: 'label_set' },
    }
  }

  return {
    data: {
      id: fieldId,
      type: 'custom_field',
      attributes: {
        display_name: name,
        metadata,
        description,
        is_deactivated: isDeactivated,
        is_color_disabled: isColorDisabled,
        is_cascading: isCascading,
        default_notification_mode: defaultNotificationMode,
      },
      relationships,
    },
  }
}

export function useUpdateCustomField(workspaceUUID: SimpleWorkspace['uuid']) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: RequestBodyParams) =>
      customFields.update({
        params: { path: { custom_field_id: params.fieldId }, query: {} },
        requestBody: buildRequestBody(params),
      }),
    onMutate({ fieldId, ...attributes }) {
      queryClient.setQueryData<
        OpalApiResponseDocument<'ReadCustomFieldsV3', 200>
      >(
        globalQueryKeys.customFields(
          workspaceUUID,
          customFieldsCollectionParams(workspaceUUID)
        ),
        (customFieldsDocument) => {
          return produce(customFieldsDocument, (draftCustomFieldsDocument) => {
            if (!draftCustomFieldsDocument) {
              return draftCustomFieldsDocument
            }
            const field = draftCustomFieldsDocument.data.find(
              (f) => f.id === fieldId
            )

            if (!field) {
              return draftCustomFieldsDocument
            }
            field.attributes = { ...field.attributes, ...attributes }
            return draftCustomFieldsDocument
          })
        }
      )
    },
    onSuccess() {
      queryClient.invalidateQueries(globalQueryKeys.customFields(workspaceUUID))
      // the plan table's view_columns query embeds each field's metadata
      // (options, display name), so it needs a refresh on any CF edit.
      queryClient.invalidateQueries({ queryKey: ['view_columns'] })
    },
  })
}
